<template>
    <div>
        <app-template>

            <!-- HEADER -->
            <div class="flex items-center justify-between mb-6">
                <div class="flex items-center gap-3">
                    <div class="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                        <i class="fas fa-search"></i>
                    </div>
                    <div>
                        <h1 class="text-xl font-bold text-gray-800 leading-tight">Détecteurs de fuites</h1>
                        <p class="text-xs text-gray-400 mt-0.5">
                            {{ detecteurs.length }} appareil{{ detecteurs.length > 1 ? 's' : '' }} enregistré{{ detecteurs.length > 1 ? 's' : '' }}
                        </p>
                    </div>
                </div>
                <button
                    class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors cursor-pointer border-none"
                    @click="openModal()"
                >
                    <i class="fas fa-plus"></i>
                    Ajouter un détecteur
                </button>
            </div>

            <!-- LOADING -->
            <div v-if="loadingGetData" class="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
                <i class="fas fa-spinner fa-spin text-lg"></i>
                <span>Chargement…</span>
            </div>

            <div v-if="!loadingGetData">

                <!-- Alert strip -->
                <div v-if="detecteursEnAlerte.length" class="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm mb-4">
                    <i class="fas fa-exclamation-triangle flex-shrink-0"></i>
                    <span>
                        <strong>{{ detecteursEnAlerte.length }} détecteur{{ detecteursEnAlerte.length > 1 ? 's' : '' }}</strong>
                        nécessite{{ detecteursEnAlerte.length > 1 ? 'nt' : '' }} une attention particulière.
                    </span>
                </div>

                <!-- Table -->
                <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table class="min-w-full text-sm">
                        <thead class="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Marque / Série</th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Assigné à</th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Étalonnage</th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Fin de validité</th>
                                <th class="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="detecteur in detecteurs"
                                :key="detecteur.id"
                                class="border-t border-gray-100 cursor-pointer transition-colors"
                                :class="rowHoverClass(detecteur)"
                                @click="openModal(detecteur)"
                            >
                                <!-- Statut -->
                                <td class="px-4 py-3.5">
                                    <span
                                        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                                        :class="statusBadgeClass(detecteur)"
                                    >
                                        <i :class="statusIcon(detecteur)"></i>
                                        {{ statusLabel(detecteur) }}
                                    </span>
                                </td>

                                <!-- Marque / Série -->
                                <td class="px-4 py-3.5">
                                    <span class="block font-semibold text-gray-800">{{ detecteur.marque }}</span>
                                    <span class="block text-xs text-gray-400 mt-0.5">{{ detecteur.num_serie }}</span>
                                </td>

                                <!-- Tech -->
                                <td class="px-4 py-3.5">
                                    <div v-if="getTechName(detecteur.tech_id)" class="inline-flex items-center gap-2 text-sm">
                                        <span class="w-6 h-6 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                            {{ getTechInitials(detecteur.tech_id) }}
                                        </span>
                                        {{ getTechName(detecteur.tech_id) }}
                                    </div>
                                    <span v-else class="text-gray-300">—</span>
                                </td>

                                <!-- Étalonnage -->
                                <td class="px-4 py-3.5 text-gray-600">{{ detecteur.date_etalonage | momentDate }}</td>

                                <!-- Fin validité -->
                                <td class="px-4 py-3.5">
                                    <span :class="isExpired(detecteur.date_fin_validite) ? 'text-red-600 font-semibold' : 'text-gray-600'">
                                        {{ detecteur.date_fin_validite | momentDate }}
                                    </span>
                                </td>

                                <!-- Actions -->
                                <td class="px-4 py-3.5 text-right" @click.stop>
                                    <button
                                        class="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors border-none bg-transparent cursor-pointer"
                                        @click="remove(detecteur)"
                                        title="Supprimer"
                                    >
                                        <i class="fas fa-trash text-sm"></i>
                                    </button>
                                </td>
                            </tr>

                            <!-- Empty state -->
                            <tr v-if="!detecteurs.length">
                                <td colspan="6" class="py-16 text-center text-gray-400">
                                    <i class="fas fa-search-minus text-3xl block mb-3 text-gray-300"></i>
                                    <p class="mb-4 text-sm">Aucun détecteur enregistré</p>
                                    <button
                                        class="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors border-none cursor-pointer"
                                        @click="openModal()"
                                    >
                                        <i class="fas fa-plus"></i> Ajouter
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- ══════════════ MODAL ══════════════ -->
            <modal name="detecteur-modal" height="auto" :adaptive="true">
                <div class="flex flex-col max-h-[92vh] text-sm">

                    <!-- Modal header -->
                    <div class="flex items-start gap-3 px-6 py-5 border-b border-gray-100 relative">
                        <div class="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center text-base flex-shrink-0">
                            <i class="fas fa-search"></i>
                        </div>
                        <div>
                            <h2 class="text-base font-bold text-gray-800 m-0 leading-tight">
                                {{ form.id ? 'Modifier le détecteur' : 'Nouveau détecteur' }}
                            </h2>
                            <p class="text-xs text-gray-400 mt-0.5">
                                {{ form.id ? 'Mettez à jour les informations' : "Renseignez les informations de l'appareil" }}
                            </p>
                        </div>
                        <button
                            class="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors border-none bg-transparent cursor-pointer"
                            @click="$modal.hide('detecteur-modal')"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <!-- Modal body -->
                    <div class="overflow-y-auto px-6 py-5 flex flex-col gap-5">

                        <!-- Section: Identification -->
                        <div class="flex flex-col gap-3">
                            <div class="text-xs font-bold uppercase tracking-widest text-gray-400 pb-1 border-b border-gray-100">
                                Identification
                            </div>
                            <div class="grid grid-cols-2 gap-3">
                                <div class="flex flex-col gap-1.5">
                                    <label class="text-xs font-semibold text-gray-600">Marque</label>
                                    <input
                                        v-model="form.marque"
                                        id="inp-marque"
                                        type="text"
                                        placeholder="ex. Bacharach, Testo…"
                                        class="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition w-full"
                                    />
                                </div>
                                <div class="flex flex-col gap-1.5">
                                    <label class="text-xs font-semibold text-gray-600">Numéro de série</label>
                                    <input
                                        v-model="form.num_serie"
                                        id="inp-num_serie"
                                        type="text"
                                        placeholder="ex. SN-2024-001"
                                        class="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition w-full"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Section: Étalonnage -->
                        <div class="flex flex-col gap-3">
                            <div class="text-xs font-bold uppercase tracking-widest text-gray-400 pb-1 border-b border-gray-100">
                                Étalonnage
                            </div>

                            <!-- Date étalonnage -->
                            <div class="flex flex-col gap-1.5">
                                <label class="text-xs font-semibold text-gray-600">Date d'étalonnage</label>
                                <input
                                    @input="setDateEtalonage"
                                    v-model="form.date_etalonage"
                                    :max="maxDate"
                                    id="inp-date-etalonage"
                                    type="date"
                                    class="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition w-full"
                                />
                            </div>

                            <!-- Fin de validité -->
                            <div class="flex flex-col gap-1.5">
                                <label class="text-xs font-semibold text-gray-600">Fin de validité</label>

                                <!-- Presets -->
                                <div v-if="form.date_etalonage" class="flex flex-wrap gap-1.5">
                                    <button
                                        v-for="preset in validityPresets"
                                        :key="preset.label"
                                        type="button"
                                        class="px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer"
                                        :class="isActivePreset(preset)
                                            ? 'bg-blue-600 border-blue-600 text-white'
                                            : 'bg-white border-gray-200 text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50'"
                                        @click="applyPreset(preset)"
                                    >
                                        {{ preset.label }}
                                    </button>
                                </div>
                                <p v-else class="flex items-center gap-1.5 text-xs text-gray-400 m-0">
                                    <i class="fas fa-info-circle"></i>
                                    Renseignez la date d'étalonnage pour utiliser les raccourcis.
                                </p>

                                <input
                                    v-model="form.date_fin_validite"
                                    id="inp-date-fin-validite"
                                    type="date"
                                    class="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition w-full"
                                />

                                <!-- Duration indicator -->
                                <div v-if="durationLabel" class="inline-flex items-center gap-1.5 text-xs text-blue-600 mt-0.5">
                                    <i class="fas fa-clock"></i>
                                    {{ durationLabel }}
                                </div>
                            </div>
                        </div>

                        <!-- Section: Technicien (caché si 1 seul tech) -->
                        <div v-if="techniciens.length > 1" class="flex flex-col gap-3">
                            <div class="text-xs font-bold uppercase tracking-widest text-gray-400 pb-1 border-b border-gray-100">
                                Technicien assigné
                            </div>

                            <!-- 2–5 techs → pilules cliquables -->
                            <div v-if="techniciens.length <= 5" class="flex flex-wrap gap-2">
                                <!-- Aucun -->
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 text-sm transition-all cursor-pointer"
                                    :class="form.tech_id === null
                                        ? 'border-gray-400 bg-gray-100 text-gray-600 font-semibold'
                                        : 'border-gray-200 bg-white text-gray-400 hover:border-gray-300'"
                                    @click="form.tech_id = null"
                                >
                                    <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs flex-shrink-0">
                                        <i class="fas fa-user-slash text-xs"></i>
                                    </span>
                                    <span class="whitespace-nowrap">Aucun</span>
                                </button>
                                <!-- Techs -->
                                <button
                                    v-for="tech in techniciens"
                                    :key="tech.id"
                                    type="button"
                                    class="inline-flex items-center gap-2 px-3 py-2 rounded-full border-2 text-sm transition-all cursor-pointer"
                                    :class="form.tech_id === tech.id
                                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                                        : 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:text-blue-600'"
                                    @click="form.tech_id = tech.id"
                                >
                                    <span class="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                        {{ initiales(tech) }}
                                    </span>
                                    <span class="whitespace-nowrap">{{ tech.prenom }} {{ tech.nom }}</span>
                                </button>
                            </div>

                            <!-- 6+ techs → liste scrollable avec recherche -->
                            <div v-else class="border border-gray-200 rounded-xl overflow-hidden">
                                <!-- Champ de recherche -->
                                <div class="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-100 text-gray-400">
                                    <i class="fas fa-search text-xs flex-shrink-0"></i>
                                    <input
                                        v-model="techSearch"
                                        type="text"
                                        placeholder="Rechercher un technicien…"
                                        class="border-none outline-none bg-transparent text-sm text-gray-800 w-full placeholder-gray-400"
                                    />
                                </div>
                                <!-- Liste -->
                                <div class="max-h-48 overflow-y-auto">
                                    <!-- Aucun -->
                                    <button
                                        type="button"
                                        class="flex items-center gap-2.5 w-full text-left px-3 py-2.5 border-none border-b border-gray-50 text-sm transition-colors cursor-pointer"
                                        :class="form.tech_id === null
                                            ? 'bg-blue-50 text-blue-700 font-semibold'
                                            : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'"
                                        @click="form.tech_id = null"
                                    >
                                        <span class="w-7 h-7 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-xs flex-shrink-0">
                                            <i class="fas fa-user-slash"></i>
                                        </span>
                                        Aucun technicien
                                    </button>
                                    <!-- Techs filtrés -->
                                    <button
                                        v-for="tech in filteredTechniciens"
                                        :key="tech.id"
                                        type="button"
                                        class="flex items-center gap-2.5 w-full text-left px-3 py-2.5 border-none border-b border-gray-50 text-sm transition-colors cursor-pointer"
                                        :class="form.tech_id === tech.id
                                            ? 'bg-blue-50 text-blue-700 font-semibold'
                                            : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'"
                                        @click="form.tech_id = tech.id"
                                    >
                                        <span class="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                            {{ initiales(tech) }}
                                        </span>
                                        {{ tech.prenom }} {{ tech.nom }}
                                    </button>
                                    <!-- Aucun résultat -->
                                    <div v-if="filteredTechniciens.length === 0" class="px-3 py-4 text-center text-xs text-gray-400 italic">
                                        Aucun résultat pour "{{ techSearch }}"
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    <!-- Modal footer -->
                    <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
                        <button
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors cursor-pointer"
                            @click="$modal.hide('detecteur-modal')"
                        >
                            Annuler
                        </button>
                        <button
                            :disabled="loadingSave"
                            class="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors border-none cursor-pointer"
                            @click="save"
                        >
                            <i v-if="loadingSave" class="fas fa-spinner fa-spin"></i>
                            <template v-if="!loadingSave">
                                <i class="fas fa-check"></i>
                                Enregistrer
                            </template>
                        </button>
                    </div>

                </div>
            </modal>

        </app-template>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    data() {
        return {
            detecteurs: [],
            techniciens: [],
            techSearch: '',
            form: {
                id: null,
                marque: '',
                num_serie: '',
                tech_id: null,
                date_etalonage: null,
                date_fin_validite: null,
            },
            validityPresets: [
                { label: '1 mois',  months: 1  },
                { label: '3 mois',  months: 3  },
                { label: '6 mois',  months: 6  },
                { label: '1 an',    months: 12 },
                { label: '2 ans',   months: 24 },
                { label: '3 ans',   months: 36 },
            ],
            loadingGetData: false,
            loadingSave: false,
        }
    },

    filters: {
        momentDate(value) {
            if (!value) return '-'
            return moment(value).format('DD/MM/YYYY')
        }
    },

    computed: {
        maxDate() {
            return moment().format('YYYY-MM-DD')
        },

        detecteursEnAlerte() {
            return this.detecteurs.filter(d => {
                if (!d.date_fin_validite) return false
                const days = Math.ceil((new Date(d.date_fin_validite) - new Date()) / 86400000)
                return days < 30
            })
        },

        filteredTechniciens() {
            const sorted = [...this.techniciens].sort((a, b) =>
                `${a.nom}${a.prenom}`.localeCompare(`${b.nom}${b.prenom}`)
            )
            if (!this.techSearch.trim()) return sorted
            const q = this.techSearch.toLowerCase()
            return sorted.filter(t =>
                `${t.prenom} ${t.nom}`.toLowerCase().includes(q)
            )
        },

        durationLabel() {
            if (!this.form.date_etalonage || !this.form.date_fin_validite) return null
            const start = moment(this.form.date_etalonage)
            const end   = moment(this.form.date_fin_validite)
            if (!end.isAfter(start)) return null
            const months = end.diff(start, 'months')
            const days   = end.diff(start, 'days')
            if (months >= 12 && months % 12 === 0) return `Validité : ${months / 12} an${months / 12 > 1 ? 's' : ''}`
            if (months > 0) return `Validité : ${months} mois`
            return `Validité : ${days} jour${days > 1 ? 's' : ''}`
        },
    },

    methods: {
        async fetchDetecteurs() {
            this.loadingGetData = true
            const res = await this.$request.get('/detecteurs/list')
            this.detecteurs = res
            this.loadingGetData = false
        },

        async fetchTechniciens() {
            const res = await this.$request.get('/techniciens')
            this.techniciens = res
        },

        openModal(detecteur = null) {
            this.techSearch = ''
            this.form = detecteur
                ? { ...detecteur }
                : {
                    id: null,
                    marque: '',
                    num_serie: '',
                    tech_id: this.techniciens.length === 1 ? this.techniciens[0].id : null,
                    date_etalonage: null,
                    date_fin_validite: null,
                }
            this.form.date_etalonage    = this.form.date_etalonage    ? moment(this.form.date_etalonage).format('YYYY-MM-DD')    : null
            this.form.date_fin_validite = this.form.date_fin_validite ? moment(this.form.date_fin_validite).format('YYYY-MM-DD') : null
            this.$modal.show('detecteur-modal')
        },

        async save() {
            const validation = this.$inpError([
                'inp-marque:required|min=2',
                'inp-num_serie:required|min=2',
                'inp-date-etalonage:required',
                'inp-date-fin-validite:required',
            ])
            if (!validation) { this.$toast.error('Formulaire invalide'); return }
            if (this.form.date_etalonage && this.form.date_fin_validite) {
                if (new Date(this.form.date_fin_validite) <= new Date(this.form.date_etalonage)) {
                    this.$toast.error("La date de fin de validité doit être après la date d'étalonnage.")
                    return
                }
            }
            const route = this.form.id ? '/detecteurs/update' : '/detecteurs/store'
            this.loadingSave = true
            await this.$request.post(route, { ...this.form, societe_id: this.societeId })
            await this.fetchDetecteurs()
            this.$modal.hide('detecteur-modal')
            this.loadingSave = false
            this.$eventHub.$emit('refresh-pastilles', 'detecteurs')
            this.$eventHub.$emit('refresh-warnings')
        },

        remove(detecteur) {
            if (!confirm('Supprimer ce détecteur ?')) return
            this.$request.post('/detecteurs/delete', { id: detecteur.id }).then(() => this.fetchDetecteurs())
        },

        getTechName(id) {
            const t = this.techniciens.find(t => t.id === id)
            return t ? `${t.prenom} ${t.nom}` : null
        },

        getTechInitials(id) {
            const t = this.techniciens.find(t => t.id === id)
            return t ? this.initiales(t) : '?'
        },

        initiales(tech) {
            return `${(tech.prenom || '')[0] || ''}${(tech.nom || '')[0] || ''}`.toUpperCase()
        },

        isExpired(date) {
            if (!date) return false
            return new Date(date) < new Date()
        },

        setDateEtalonage() {
            if (!this.form.date_etalonage) return
            this.applyPreset({ months: 12 })
        },

        applyPreset(preset) {
            if (!this.form.date_etalonage) return
            this.form.date_fin_validite = moment(this.form.date_etalonage).add(preset.months, 'months').format('YYYY-MM-DD')
        },

        isActivePreset(preset) {
            if (!this.form.date_etalonage || !this.form.date_fin_validite) return false
            return this.form.date_fin_validite === moment(this.form.date_etalonage).add(preset.months, 'months').format('YYYY-MM-DD')
        },

        statusLevel(detecteur) {
            if (!detecteur.date_fin_validite) return 'none'
            const days = Math.ceil((new Date(detecteur.date_fin_validite) - new Date()) / 86400000)
            if (days < 0)  return 'expired'
            if (days < 30) return 'warning'
            return 'ok'
        },

        statusLabel(detecteur) {
            const lvl = this.statusLevel(detecteur)
            if (lvl === 'expired') return 'Expiré'
            if (lvl === 'warning') {
                const days = Math.ceil((new Date(detecteur.date_fin_validite) - new Date()) / 86400000)
                return `${days}j`
            }
            return 'Valide'
        },

        statusIcon(detecteur) {
            const lvl = this.statusLevel(detecteur)
            if (lvl === 'expired') return 'fas fa-times-circle'
            if (lvl === 'warning') return 'fas fa-exclamation-triangle'
            return 'fas fa-check-circle'
        },

        statusBadgeClass(detecteur) {
            const lvl = this.statusLevel(detecteur)
            if (lvl === 'ok')      return 'bg-green-50 text-green-700'
            if (lvl === 'warning') return 'bg-amber-50 text-amber-700'
            if (lvl === 'expired') return 'bg-red-50 text-red-600'
            return 'bg-gray-100 text-gray-400'
        },

        rowHoverClass(detecteur) {
            const lvl = this.statusLevel(detecteur)
            if (lvl === 'warning') return 'hover:bg-amber-50'
            if (lvl === 'expired') return 'hover:bg-red-50'
            return 'hover:bg-gray-50'
        },
    },

    mounted() {
        this.fetchDetecteurs()
        this.fetchTechniciens()
    },
}
</script>